'use client';

import { useCallback, useState } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import {
  markdownHref,
  type MarkdownExportSpec,
} from '@/lib/utils/markdown-export-path';

function getBasePath(): string {
  const bp = process.env.NEXT_PUBLIC_BASE_PATH || '/academy';
  return bp.endsWith('/') ? bp.slice(0, -1) : bp;
}

export function MarkdownAskAiToolbar({
  spec,
  variant = 'default',
}: {
  spec: MarkdownExportSpec;
  variant?: 'default' | 'inline';
}) {
  const href = markdownHref(spec);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const handleCopyForLlm = useCallback(async () => {
    const base = getBasePath();
    const url = `${base}${href}`;
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const text = await res.text();
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2500);
    }
  }, [href]);

  const isInline = variant === 'inline';

  /** Match `cs-caption` stats row (see `styles/typography.css`). */
  const controlTypo =
    'cs-caption m-0 cursor-pointer border-0 bg-transparent font-normal text-[var(--Text-Default-Light)] no-underline transition-colors hover:text-text-default-strong';

  /** Same vertical rule as course meta / LP stats (`styles/generic.css` `.divider`). */
  const ToolbarDivider = () => (
    <div className="relative hidden md:block" aria-hidden>
      <span className="divider" />
    </div>
  );

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-0 rounded-lg border border-border-light',
        isInline
          ? 'shrink-0 border-transparent bg-transparent'
          : 'mb-4 bg-surface-1',
      )}
      role="toolbar"
      aria-label="Markdown and AI helpers"
    >
      <button
        type="button"
        onClick={handleCopyForLlm}
        className={cn(
          controlTypo,
          'inline-flex items-center gap-1',
          isInline ? 'rounded-l-md px-2 py-1' : 'rounded-l-lg px-3 py-2',
        )}
        aria-label="Copy page as Markdown for LLM"
      >
        <MaterialIcons
          iconName="copy_all"
          className="!text-[16px] !leading-none text-icon-subtle"
        />
        {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy for LLM'}
      </button>
      <ToolbarDivider />
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          controlTypo,
          'inline-flex items-center gap-1',
          isInline ? 'rounded-r-md px-2 py-1' : 'rounded-r-lg px-3 py-2 sm:rounded-r-lg',
        )}
        aria-label="View this page as Markdown in a new tab"
      >
        <MaterialIcons
          iconName="code_blocks"
          className="!text-[16px] !leading-none text-icon-subtle"
        />
        View as Markdown
      </Link>
    </div>
  );
}
